/**
 * Uma aba só para TODAS as conversas no acesso rápido.
 *
 * ## O que havia
 *
 * Cada conversa aberta (ou distribuída automaticamente) virava uma aba. Numa
 * manhã de movimento isso enche a barra: as abas encolhem, o excedente cai no
 * menu "+N", e as páginas de verdade (Chamados, Filtros, Mural) são empurradas
 * para fora da vista por conversas que o operador já respondeu.
 *
 * ## O que a Gisele pediu (19/10/2026)
 *
 * "QuickAccess: criar guia única com badge" -- e, na confirmação do dono do
 * produto: a aba **abre a página de chat normal, com a lista que já existe
 * hoje**, e isso vale **somente para os chats**. Chamados, páginas e o Orby
 * continuam cada um com a sua aba.
 *
 * Então: zero conversas, nenhuma aba; uma ou cem, UMA aba "Chat", com a conta
 * das que têm novidade ao lado e piscando enquanto houver alguma.
 *
 * ## Por que a conta é das NOVIDADES, e não das conversas
 *
 * "Chat · 14" com catorze conversas já lidas não informa nada -- o operador
 * sabe que tem conversa, ele está num plantão de chat. O que ele precisa saber
 * da barra é **quantas estão esperando resposta**. Sem novidade nenhuma, a aba
 * mostra só "Chat": número zero é ruído.
 *
 * ## De onde vem "tem novidade"
 *
 * Do mesmo sinal que já fazia a aba individual piscar: `newMessageChats`, o
 * conjunto vivo do `WebSocketContext` (entra em mensagem do cliente, sai
 * quando a conversa é lida), mais o `__isNew` do chat recém-distribuído que
 * ainda não recebeu mensagem. A regra de uma sala só está em
 * `utils/piscarDaAbaDeChat` e é ela que esta aba agrega -- duas definições de
 * "novidade" acabariam divergindo.
 */

import { temMensagemNova } from './piscarDaAbaDeChat';

/**
 * O `room_id` da aba agregada.
 *
 * É `room_id` (e não um `id` qualquer) porque a máquina do acesso rápido
 * identifica conversa por `room_id` em sete lugares: ordenação, remoção,
 * item ativo. Um identificador de outra forma exigiria mexer em todos eles.
 *
 * O valor é impossível como id de sala de verdade (elas são `uniqid`), para
 * nunca colidir.
 */
export const ID_DA_ABA_DE_CHAT = '__todas-as-conversas__';

/** Este item do acesso rápido é a aba agregada de chat? */
export const ehAbaUnicaDeChat = (item) => item?.room_id === ID_DA_ABA_DE_CHAT;

/**
 * Quantas conversas têm novidade para o operador.
 *
 * @param {Array} conversas as salas como estão no acesso rápido
 * @param {Set|Array} salasComMensagemNova ids com mensagem não lida
 */
export const conversasComNovidade = (conversas, salasComMensagemNova) =>
    (Array.isArray(conversas) ? conversas : []).filter(
        (c) => c && (temMensagemNova(salasComMensagemNova, c.room_id) || c.__isNew === true)
    ).length;

/**
 * As conversas do acesso rápido colapsadas em uma aba só.
 *
 * Devolve uma LISTA (vazia ou de um item) porque é isso que a máquina do
 * acesso rápido consome no lugar das conversas.
 *
 * @param {Array} conversas
 * @param {Set|Array} salasComMensagemNova
 * @returns {Array}
 */
export const colapsarConversas = (conversas, salasComMensagemNova) => {
    const lista = (Array.isArray(conversas) ? conversas : []).filter(Boolean);

    // Nenhuma conversa, nenhuma aba: a barra não ganha um controle vazio só
    // porque o operador passou pela tela de chat.
    if (lista.length === 0) return [];

    const novidades = conversasComNovidade(lista, salasComMensagemNova);

    return [{
        room_id: ID_DA_ABA_DE_CHAT,
        __agregada: true,
        __quantidade: lista.length,
        __novidades: novidades,
        /*
         * `__isNew` é o que faz a aba piscar (ver `abaDeveriaPiscar`), e aqui
         * ele é DERIVADO a cada render do sinal vivo -- não um sinalizador
         * gravado que alguém precisa lembrar de apagar. Era exatamente esse o
         * defeito do `__isNew` persistido: piscava para sempre.
         */
        __isNew: novidades > 0,
    }];
};

/**
 * O rótulo da aba.
 *
 * A conta das novidades vai no próprio texto, e não como elemento à parte, por
 * uma razão prática: a barra desenha a aba em dois caminhos diferentes (a
 * normal e a do menu "+N"), e o rótulo é o único lugar por onde a informação
 * chega aos dois de uma vez.
 */
export const rotuloDaAbaDeChat = (item) => {
    const novidades = Number(item?.__novidades) || 0;

    return novidades > 0 ? `Chat · ${novidades}` : 'Chat';
};
